import { EventType } from "@/analytics/analytics-events";
import {
  SelectColoredAnalysisColorScaleEventProperties,
  SelectColoredAnalysisRangeEventProperties,
  SelectColoredAnalysisToleranceEventProperties,
} from "@/analytics/cloud-analysis-events-properties";
import {
  getDistanceUnitSymbolText,
  isMetric,
} from "@/components/common/settings-context/distance-unit-utils";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import { DistanceInputField } from "@/components/ui/distance-input-field";
import { PointCloudObject } from "@/object-cache";
import {
  PointCloudAnalysis,
  ReferencePlaneType,
  setAnalysisColormap,
  setAnalysisTolerance,
  setAnalysisVolumeDisplayIsoLines,
  setAnalysisVolumeDisplayOffset,
} from "@/store/point-cloud-analysis-tool-slice";
import { useAppDispatch } from "@/store/store-hooks";
import {
  areColormapsSame,
  isColormapPresetName,
  PointCloudAnalysisColormapPreset,
  pointCloudAnalysisColormapPresets,
  updateAnalysisSearchDistance,
} from "@/utils/colormap-analysis-utils";
import {
  Dropdown,
  FaroSwitch,
  FaroText,
  NO_TRANSLATE_CLASS,
  TextField,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { DistanceUnitTypes } from "@faro-lotv/foundation";
import { Stack } from "@mui/system";
import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";

const colormapOptions = [
  {
    key: PointCloudAnalysisColormapPreset.rainbow,
    label: "Rainbow",
    value: PointCloudAnalysisColormapPreset.rainbow,
  },
  {
    key: PointCloudAnalysisColormapPreset.grayscale,
    label: "Grayscale",
    value: PointCloudAnalysisColormapPreset.grayscale,
  },
  {
    key: PointCloudAnalysisColormapPreset.blueGreenRed,
    label: "Blue-green-red",
    value: PointCloudAnalysisColormapPreset.blueGreenRed,
  },
  {
    key: PointCloudAnalysisColormapPreset.redGreenRed,
    label: "Red-green-red",
    value: PointCloudAnalysisColormapPreset.redGreenRed,
  },
];

export type ColormapOptionsFormsProps = {
  /** Point cloud object used by analysis */
  pointCloud: PointCloudObject;

  /** Active analysis object to be modified. */
  analysis: PointCloudAnalysis;

  /** Width of the panel (using CSS format) */
  width?: CSSProperties["width"];
};

/** @returns Colormap options UI panel */
export function ColormapOptionsForms({
  pointCloud,
  analysis,
  width,
}: ColormapOptionsFormsProps): JSX.Element {
  const dispatch = useAppDispatch();

  const [colormapName, setColormapName] = useState(() => {
    const initialName = Object.entries(pointCloudAnalysisColormapPresets).find(
      ([, value]) => areColormapsSame(value, analysis.colormap),
    );
    return initialName?.[0];
  });

  const changeColormap = useCallback(
    (newColormap: string) => {
      Analytics.track<SelectColoredAnalysisColorScaleEventProperties>(
        EventType.selectColoredAnalysisColorScale,
        {
          newValue: newColormap,
        },
      );

      setColormapName(newColormap);
      if (isColormapPresetName(newColormap)) {
        const colormap = pointCloudAnalysisColormapPresets[newColormap];
        dispatch(
          setAnalysisColormap({
            analysisId: analysis.id,
            colormap,
          }),
        );
      }
    },
    [analysis.id, dispatch],
  );

  // Keep track of latest analysis values in order to synchronize callbacks
  // to make sure searchDistance always large or equal to tolerance
  const toleranceRef = useRef(analysis.tolerance);
  const searchDistanceRef = useRef(analysis.searchDistance);
  useEffect(() => {
    toleranceRef.current = analysis.tolerance;
    searchDistanceRef.current = analysis.searchDistance;
  }, [analysis.tolerance, analysis.searchDistance]);

  const changeTolerance = useCallback(
    async (tolerance: number) => {
      Analytics.track<SelectColoredAnalysisToleranceEventProperties>(
        EventType.selectColoredAnalysisTolerance,
        { newTolerance: tolerance },
      );

      dispatch(setAnalysisTolerance({ analysisId: analysis.id, tolerance }));
      if (searchDistanceRef.current < tolerance) {
        await updateAnalysisSearchDistance(
          pointCloud,
          analysis,
          tolerance,
          dispatch,
        );
      }
    },
    [analysis, dispatch, pointCloud],
  );

  const changeRange = useCallback(
    async (searchDistance: number) => {
      Analytics.track<SelectColoredAnalysisRangeEventProperties>(
        EventType.selectColoredAnalysisRange,
        { newRange: searchDistance },
      );
      await updateAnalysisSearchDistance(
        pointCloud,
        analysis,
        searchDistance,
        dispatch,
      );
      if (toleranceRef.current > searchDistance) {
        dispatch(
          setAnalysisTolerance({
            analysisId: analysis.id,
            tolerance: searchDistance,
          }),
        );
      }
    },
    [analysis, dispatch, pointCloud],
  );

  const distanceUnit = useDistanceUnit();

  const activeUnit = useMemo(
    () =>
      isMetric(distanceUnit)
        ? DistanceUnitTypes.millimeters
        : DistanceUnitTypes.decimalInches,
    [distanceUnit],
  );

  // Tolerance computation is expensive, to avoid temporary changes of text field while editing,
  // wait 750ms before report last update
  const textInputDebounceTime = 750;

  const volumeAnalysisMode =
    analysis.referencePlaneType === ReferencePlaneType.volume;

  const changeIsoLines = useCallback(
    (isoLinesText: string) => {
      const isoLines = parseInt(isoLinesText, 10);
      if (isNaN(isoLines) || isoLines < 0) {
        return;
      }
      dispatch(
        setAnalysisVolumeDisplayIsoLines({
          analysisId: analysis.id,
          nbIsoLines: isoLines,
        }),
      );
    },
    [analysis.id, dispatch],
  );

  const toggleLiftupPreview = useCallback(() => {
    if (analysis.volumeAnalysisResult === undefined) return;

    // Initial liftup the whole volume mesh 1 meter above the height point
    const LIFT_UP_OFFSET = 1.0;
    const offset =
      analysis.volumeDisplayOffset === undefined
        ? analysis.volumeAnalysisResult.heightMax -
          analysis.volumeAnalysisResult.heightMin +
          LIFT_UP_OFFSET
        : undefined;
    dispatch(
      setAnalysisVolumeDisplayOffset({
        analysisId: analysis.id,
        offset,
      }),
    );
  }, [
    analysis.volumeAnalysisResult,
    analysis.volumeDisplayOffset,
    analysis.id,
    dispatch,
  ]);

  return (
    <Stack spacing={1} sx={{ width }}>
      <Dropdown
        variant="underlined"
        options={colormapOptions}
        value={colormapName}
        dark
        onChange={(e) => changeColormap(e.target.value)}
      />

      {volumeAnalysisMode && (
        <>
          <Stack
            direction="row"
            sx={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <FaroText variant="labelL" dark>
              Contour lines:
            </FaroText>
            <TextField
              variant="underlined"
              fullWidth
              dark
              type="number"
              text={(analysis.volumeDisplayNbIsoLines ?? 0).toString()}
              onTextChanged={changeIsoLines}
            />
          </Stack>
          <Stack
            direction="row"
            sx={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <FaroText variant="labelL" dark>
              Raise mesh
            </FaroText>
            <FaroSwitch
              dark
              checked={analysis.volumeDisplayOffset !== undefined}
              onToggle={toggleLiftupPreview}
            />
          </Stack>
        </>
      )}

      {!volumeAnalysisMode && (
        <>
          <Stack
            direction="row"
            sx={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <FaroText variant="labelL" dark>
              Tolerance:
            </FaroText>
            <DistanceInputField
              variant="underlined"
              fullWidth
              dark
              distanceInMeter={analysis.tolerance}
              distanceUnit={activeUnit}
              errorMessage="Invalid number"
              onDistanceChanged={changeTolerance}
              debounceMs={textInputDebounceTime}
              inputProps={{ style: { textAlign: "right" } }}
            />
            <FaroText variant="labelL" className={NO_TRANSLATE_CLASS} dark>
              {getDistanceUnitSymbolText(activeUnit)}
            </FaroText>
          </Stack>
          <Stack
            direction="row"
            sx={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <FaroText variant="labelL" dark>
              Range:
            </FaroText>
            <DistanceInputField
              variant="underlined"
              fullWidth
              dark
              distanceInMeter={analysis.searchDistance}
              distanceUnit={activeUnit}
              errorMessage="Invalid number"
              onDistanceChanged={changeRange}
              debounceMs={textInputDebounceTime}
              inputProps={{ style: { textAlign: "right" } }}
            />
            <FaroText variant="labelL" className={NO_TRANSLATE_CLASS} dark>
              {getDistanceUnitSymbolText(activeUnit)}
            </FaroText>
          </Stack>
        </>
      )}
    </Stack>
  );
}
